import React from "react";
import { insights } from "../mock";
import { Link } from "react-router-dom";
import { Clock, ArrowRight, Search, Lock } from "lucide-react";
import useSeo from "../hooks/useSeo";

const Insights = () => {
  useSeo({
    title: "Insights & Research Notes",
    description:
      "Field notes, market reports and playbooks from the Astoria Solutions team across Research, Finance and Auction advisory.",
    path: "/insights",
  });
  const [q, setQ] = React.useState("");
  const [cat, setCat] = React.useState("All");
  const categories = ["All", ...new Set(insights.map((i) => i.category))];

  const filtered = insights.filter((i) => {
    const okC = cat === "All" || i.category === cat;
    const okQ = i.title.toLowerCase().includes(q.toLowerCase()) || i.excerpt.toLowerCase().includes(q.toLowerCase());
    return okC && okQ;
  });

  return (
    <>
      <section className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold">Insights & Research Notes</h1>
          <p className="mt-4 text-teal-400">Field notes, market reports and playbooks from the Astoria team.</p>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <div className="flex gap-2 flex-wrap">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                    cat === c
                      ? "bg-teal-500 border-teal-500 text-white"
                      : "bg-white border-neutral-200 text-neutral-700 hover:border-teal-400 hover:text-teal-600"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search insights"
                className="w-full pl-9 pr-3 py-2 rounded-full border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-teal-400"
              />
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((post) => (
              <Link
                key={post.slug}
                to={`/insights/${post.slug}`}
                className="group bg-white rounded-2xl overflow-hidden border border-neutral-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 block"
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className="absolute top-3 left-3 bg-teal-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    {post.category}
                  </span>
                  {post.premium && (
                    <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-amber-500 text-black text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full">
                      <Lock className="w-3 h-3" /> PREMIUM
                    </span>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-neutral-500">
                    <span>{post.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime}</span>
                  </div>
                  <h3 className="mt-2 text-lg font-bold text-neutral-900 leading-snug group-hover:text-teal-600 transition-colors">
                    {post.title}
                  </h3>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{post.excerpt}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-neutral-500">By {post.author}</span>
                    <span className="inline-flex items-center gap-1 text-teal-600 text-sm font-semibold group-hover:gap-2 transition-all">
                      Read <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="text-center text-neutral-500 mt-12">No insights match your search.</p>
          )}
        </div>
      </section>
    </>
  );
};

export default Insights;
